import Ember from 'ember';
import { restartableTask } from 'ember-concurrency';
import IntlService from 'ember-intl/services/intl';
import { IBvTableSort } from 'volta/components/bv-table';
// @ts-ignore
import FormatCell from 'volta/components/cell/format-cell';
// @ts-ignore
import LabelCell from 'volta/components/cell/label-cell';
import SkuLocation from 'volta/models/sku-location';
import StoreService from 'volta/services/store';
import { CollectionResourceDoc } from 'volta/utils/api/jsonapi-types';
import { normalize } from 'volta/utils/api/serialize-and-push';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { sum } from 'volta/utils/math-utils';
import { colorForCategory } from 'volta/utils/style-utils';

import { action } from '@ember/object';
import { sort } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

interface IArgs {
  skuId: string;
  onClose?: () => void;
}

export default class SkuDetailsStockDetails extends Component<IArgs> {
  // Services
  // ~~~~~

  @service declare store: StoreService;
  @service declare intl: IntlService;

  // Properties
  // ~~~~~

  @tracked sortColumn = 'workshopInfo.description';
  @tracked sortAscending = true;
  @tracked locations: SkuLocation[] = [];
  @tracked sorts: IBvTableSort[] = [{ valuePath: 'workshopInfo.description', isAscending: true }];

  // Computed Properties
  // ~~~~~

  @sort('locations', 'sortProperties')
  declare sortedLocations: SkuLocation[];

  get totalOnHand() {
    return sum(this.locations.mapBy('onHand').toArray());
  }

  get sortProperties() {
    return this.sorts.map((s) => {
      return `${s.valuePath}:${s.isAscending ? 'asc' : 'desc'}`;
    });
  }

  get columns() {
    const intl = this.intl;

    return [
      {
        columnName: intl.t('workshop'),
        valuePath: 'workshopInfo.description',
        width: 100,
        isReorderable: true,
        isResizable: true
      },
      {
        columnName: intl.t('status'),
        valuePath: 'status',
        width: 60,
        isReorderable: true,
        isResizable: true,
        cellComponent: LabelCell,
        format: (model: SkuLocation) => {
          const status = model.status;
          return {
            label: intl.t(`skuLocationStatuses.${status}`),
            color: colorForCategory(status)
          };
        }
      },
      {
        columnName: intl.t('location'),
        valuePath: 'location',
        width: 100,
        isReorderable: true,
        isResizable: true
      },
      {
        columnName: intl.t('batchNb'),
        valuePath: 'batchNumber',
        width: 100,
        isReorderable: true,
        isResizable: true
      },
      {
        columnName: intl.t('onHand'),
        valuePath: 'onHand',
        width: 80,
        textAlign: 'right',
        isReorderable: true,
        isResizable: true,
        cellComponent: FormatCell,
        format: ({ onHand }: SkuLocation) => {
          return {
            value: onHand,
            dataType: 'number'
          };
        }
      }
    ];
  }

  // Lifecycle Hooks
  // ~~~~~

  @action
  handleDidInsert() {
    this.getWorkshopsCapacity.perform(this.args.skuId);
  }

  // Private Callbacks/Handlers
  // ~~~~~

  getWorkshopsCapacity = restartableTask(async (skuId: string) => {
    try {
      const response: CollectionResourceDoc = await this.store.customQuery(
        'sku',
        'locations',
        skuId,
        undefined
      );

      if (response && response.data.length) {
        this.locations = (normalize('planning', response) as Ember.Array<SkuLocation>).toArray();
      } else {
        this.locations = [];
      }
    } catch (error) {
      return defaultErrorHandler(error);
    }
  });
}
